import { useEffect, useRef, useState } from 'react';
import { staffApi, formatApiError, viewProtectedFile } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FileText, Upload, Download, Trash2 } from 'lucide-react';

export const DOC_CATEGORIES = ['Hiring Paperwork', 'ID / Personal', 'Work Permit', 'Certificate', 'Contract', 'Other'];

// Reusable documents list + upload used by both the admin Staff Profile (basePath /staff/:id) and
// the staff self-service portal (basePath /staff/me). canDelete/canUpload gate the affordances.
export function DocumentsPanel({ basePath, canUpload = true, canDelete = false, emptyLabel = 'No documents uploaded yet.', api = staffApi }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Other');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    api.get(`${basePath}/documents`).then(({ data }) => setDocuments(data.documents)).finally(() => setLoading(false));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePath]);

  const submit = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error('Please choose a file to upload');
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('title', title.trim() || file.name);
      form.append('category', category);
      await api.post(`${basePath}/documents`, form);
      toast.success('Document uploaded');
      setOpen(false);
      setTitle('');
      setCategory('Other');
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (docId) => {
    try {
      await api.delete(`${basePath}/documents/${docId}`);
      toast.success('Document removed');
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const view = async (docId) => {
    try {
      await viewProtectedFile(api, `/staff/documents/${docId}/download`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="bg-white rounded-xl border border-border/40 p-5" data-testid="documents-panel">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading font-700 text-charcoal flex items-center gap-2">
          <FileText size={16} className="text-coriander" /> Documents &amp; HR
        </h2>
        {canUpload && (
          <Button data-testid="document-upload-open-btn" size="sm" onClick={() => setOpen(true)} className="bg-saffron hover:bg-saffron-hover gap-1.5">
            <Upload size={15} /> Upload
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-muted-ink py-2">Loading…</p>
      ) : documents.length === 0 ? (
        <p className="text-sm text-muted-ink py-2">{emptyLabel}</p>
      ) : (
        <div className="divide-y divide-border/30">
          {documents.map((d) => (
            <div key={d.id} className="py-3 flex items-center justify-between gap-3" data-testid="document-row">
              <div className="min-w-0">
                <p className="text-sm font-medium text-charcoal truncate">{d.title}</p>
                <p className="text-xs text-muted-ink">
                  {d.category ? `${d.category} · ` : ''}{d.fileName} · {new Date(d.createdAt).toLocaleDateString('en-CA')}
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button variant="ghost" size="sm" data-testid="document-download-btn" onClick={() => view(d.id)} className="text-coriander gap-1.5">
                  <Download size={15} /> View
                </Button>
                {canDelete && (
                  <Button variant="ghost" size="sm" data-testid="document-delete-btn" onClick={() => remove(d.id)} className="text-destructive">
                    <Trash2 size={15} />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="document-upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input data-testid="document-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Work Permit 2026" />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="document-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>File</Label>
              <input
                ref={fileRef}
                type="file"
                data-testid="document-file-input"
                className="block w-full text-sm text-charcoal file:mr-3 file:rounded-lg file:border-0 file:bg-saffron file:px-3 file:py-1.5 file:text-white file:text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="document-upload-submit-btn" disabled={busy} onClick={submit} className="bg-saffron hover:bg-saffron-hover">
              {busy ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
